import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { articles as articlesData } from '../mocks/articles';
import { mockNotes as notesData, Note } from '../mocks/notes';
import { portfolioWorks, portfolioCategories } from '../mocks/portfolio';
import { progressData } from '../mocks/progress';

interface Article {
  id: number;
  title: string;
  summary: string;
  category: string;
  coverImage: string;
  author: {
    name: string;
    avatar: string;
  };
  publishDate: string;
  readTime: number;
  likes: number;
  bookmarks: number;
  comments: number;
  tags: string[];
}

interface PortfolioWork {
  id: number;
  title: string;
  category: string;
  date: string;
  tools: string[];
  description: string;
  coverImage: string;
  beforeImage?: string;
  afterImage?: string;
}

interface PortfolioCategory {
  name: string;
  count: number;
}

interface CourseCategory {
  id: number;
  name: string;
  icon: string;
  hours: number;
  courses: number;
  color: string;
  courses_list: Array<{
    name: string;
    progress: number;
  }>;
}

interface SkillDirection {
  name: string;
  level: number;
  color: string;
}

interface LearningRecord {
  date: string;
  content: string;
  duration: string;
  category: string;
}

interface ProgressData {
  overallProgress: number;
  courseCategories: CourseCategory[];
  skillDirections: SkillDirection[];
  learningRecords: LearningRecord[];
  monthlyGoals: Array<{
    month: string;
    target: number;
    completed: number;
  }>;
}

interface ContentContextType {
  // Articles
  articles: Article[];
  addArticle: (article: Omit<Article, 'id'>) => void;
  updateArticle: (id: number, article: Partial<Article>) => void;
  deleteArticle: (id: number) => void;
  getArticleById: (id: number) => Article | undefined;
  
  // Notes
  notes: Note[];
  addNote: (note: Omit<Note, 'id'>) => void;
  updateNote: (id: number, note: Partial<Note>) => void;
  deleteNote: (id: number) => void;
  getNoteById: (id: number) => Note | undefined;

  // Portfolio
  portfolioWorks: PortfolioWork[];
  portfolioCategories: PortfolioCategory[];
  addPortfolioWork: (work: Omit<PortfolioWork, 'id'>) => void;
  updatePortfolioWork: (id: number, work: Partial<PortfolioWork>) => void;
  deletePortfolioWork: (id: number) => void;
  getPortfolioWorkById: (id: number) => PortfolioWork | undefined;
  updatePortfolioCategories: (categories: PortfolioCategory[]) => void;

  // Progress
  progressData: ProgressData;
  updateProgressData: (data: Partial<ProgressData>) => void;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export function ContentProvider({ children }: { children: ReactNode }) {
  const [articles, setArticles] = useState<Article[]>(() => {
    const saved = localStorage.getItem('articles');
    return saved ? JSON.parse(saved) : articlesData;
  });

  const [notes, setNotes] = useState<Note[]>(() => {
    const saved = localStorage.getItem('notes');
    return saved ? JSON.parse(saved) : notesData;
  });

  const [portfolio, setPortfolio] = useState<PortfolioWork[]>(() => {
    const saved = localStorage.getItem('portfolioWorks');
    return saved ? JSON.parse(saved) : portfolioWorks;
  });

  const [categories, setCategories] = useState<PortfolioCategory[]>(() => {
    const saved = localStorage.getItem('portfolioCategories');
    return saved ? JSON.parse(saved) : portfolioCategories;
  });

  const [progress, setProgress] = useState<ProgressData>(() => {
    const saved = localStorage.getItem('progressData');
    return saved ? JSON.parse(saved) : progressData;
  });

  useEffect(() => {
    localStorage.setItem('articles', JSON.stringify(articles));
  }, [articles]);

  useEffect(() => {
    localStorage.setItem('notes', JSON.stringify(notes));
  }, [notes]);

  useEffect(() => {
    localStorage.setItem('portfolioWorks', JSON.stringify(portfolio));
  }, [portfolio]);

  useEffect(() => {
    localStorage.setItem('portfolioCategories', JSON.stringify(categories));
  }, [categories]);

  useEffect(() => {
    localStorage.setItem('progressData', JSON.stringify(progress));
  }, [progress]);

  // Article functions
  const addArticle = (article: Omit<Article, 'id'>) => {
    const newArticle = {
      ...article,
      id: Math.max(0, ...articles.map(a => a.id)) + 1,
    };
    setArticles(prev => [newArticle, ...prev]);
  };

  const updateArticle = (id: number, article: Partial<Article>) => {
    setArticles(prev => prev.map(a => a.id === id ? { ...a, ...article } : a));
  };

  const deleteArticle = (id: number) => {
    setArticles(prev => prev.filter(a => a.id !== id));
  };

  const getArticleById = (id: number) => {
    return articles.find(a => a.id === id);
  };

  // Note functions
  const addNote = (note: Omit<Note, 'id'>) => {
    const newNote = {
      ...note,
      id: Math.max(0, ...notes.map(n => n.id)) + 1,
    };
    setNotes(prev => [newNote, ...prev]);
  };

  const updateNote = (id: number, note: Partial<Note>) => {
    setNotes(prev => prev.map(n => n.id === id ? { ...n, ...note } : n));
  };

  const deleteNote = (id: number) => {
    setNotes(prev => prev.filter(n => n.id !== id));
  };

  const getNoteById = (id: number) => {
    return notes.find(n => n.id === id);
  };

  // Portfolio functions
  const addPortfolioWork = (work: Omit<PortfolioWork, 'id'>) => {
    const newWork = {
      ...work,
      id: Math.max(0, ...portfolio.map(w => w.id)) + 1,
    };
    setPortfolio(prev => [newWork, ...prev]);
    
    // Update category count
    const categoryIndex = categories.findIndex(c => c.name === work.category);
    if (categoryIndex !== -1) {
      const newCategories = [...categories];
      newCategories[categoryIndex].count += 1;
      newCategories[0].count += 1; // 全部
      setCategories(newCategories);
    }
  };

  const updatePortfolioWork = (id: number, work: Partial<PortfolioWork>) => {
    const oldWork = portfolio.find(w => w.id === id);
    setPortfolio(prev => prev.map(w => w.id === id ? { ...w, ...work } : w));
    
    // Update category count if category changed
    if (oldWork && work.category && oldWork.category !== work.category) {
      const newCategories = [...categories];
      const oldCatIndex = newCategories.findIndex(c => c.name === oldWork.category);
      const newCatIndex = newCategories.findIndex(c => c.name === work.category);
      if (oldCatIndex !== -1) newCategories[oldCatIndex].count -= 1;
      if (newCatIndex !== -1) newCategories[newCatIndex].count += 1;
      setCategories(newCategories);
    }
  };

  const deletePortfolioWork = (id: number) => {
    const work = portfolio.find(w => w.id === id);
    setPortfolio(prev => prev.filter(w => w.id !== id));
    
    // Update category count
    if (work) {
      const newCategories = [...categories];
      const categoryIndex = newCategories.findIndex(c => c.name === work.category);
      if (categoryIndex !== -1) {
        newCategories[categoryIndex].count -= 1;
        newCategories[0].count -= 1; // 全部
        setCategories(newCategories);
      }
    }
  };

  const getPortfolioWorkById = (id: number) => {
    return portfolio.find(w => w.id === id);
  };

  const updatePortfolioCategories = (newCategories: PortfolioCategory[]) => {
    setCategories(newCategories);
  };

  // Progress functions
  const updateProgressData = (data: Partial<ProgressData>) => {
    setProgress(prev => ({ ...prev, ...data }));
  };

  return (
    <ContentContext.Provider
      value={{
        articles,
        addArticle,
        updateArticle,
        deleteArticle,
        getArticleById,
        notes,
        addNote,
        updateNote,
        deleteNote,
        getNoteById,
        portfolioWorks: portfolio,
        portfolioCategories: categories,
        addPortfolioWork,
        updatePortfolioWork,
        deletePortfolioWork,
        getPortfolioWorkById,
        updatePortfolioCategories,
        progressData: progress,
        updateProgressData,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within ContentProvider');
  }
  return context;
}